import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { Media } from '@sazerac/design-tokens';
import { ApiError } from '../api/client';

/**
 * Small shared building blocks: lazy media, reveal-on-scroll, loading / empty / error
 * states, and the section heading used throughout the design.
 */

interface MediaImageProps {
  media?: Media | null;
  alt?: string;
  className?: string;
  style?: React.CSSProperties;
  /** Above-the-fold images load eagerly; everything else is lazy. */
  priority?: boolean;
  placeholder?: string;
  zoomOnHover?: boolean;
  sizes?: string;
}

export function MediaImage({
  media,
  alt,
  className = '',
  style,
  priority = false,
  placeholder = 'Photo',
  zoomOnHover = false,
  sizes,
}: MediaImageProps) {
  const [loaded, setLoaded] = useState(false);

  if (!media?.url) {
    return (
      <div className={`sz-media ${className}`} style={style} role="img" aria-label={alt ?? placeholder}>
        <div className="sz-media__placeholder">{placeholder}</div>
      </div>
    );
  }

  return (
    <div
      className={`sz-media ${zoomOnHover ? 'sz-media--zoom' : ''} ${className}`}
      style={style}
      data-loaded={loaded}
    >
      {!loaded && <div className="sz-media__placeholder sz-skeleton" aria-hidden="true" />}
      <img
        src={media.url}
        alt={alt ?? media.altText ?? ''}
        loading={priority ? 'eager' : 'lazy'}
        decoding={priority ? 'sync' : 'async'}
        // React 18 passes through only the lowercase DOM attribute name.
        {...{ fetchpriority: priority ? 'high' : 'auto' }}
        sizes={sizes}
        width={media.width ?? undefined}
        height={media.height ?? undefined}
        onLoad={() => setLoaded(true)}
        onError={() => setLoaded(true)}
      />
    </div>
  );
}

/** Fades content up as it scrolls into view. Inert under prefers-reduced-motion. */
export function Reveal({
  children,
  delay = 0,
  className = '',
  as: Tag = 'div',
}: {
  children: ReactNode;
  delay?: number;
  className?: string;
  as?: 'div' | 'section' | 'article' | 'li';
}) {
  const ref = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setVisible(true);
            observer.unobserve(entry.target);
          }
        }
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <Tag
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ref={ref as any}
      className={`sz-reveal ${className}`}
      data-visible={visible}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </Tag>
  );
}

export function SectionHeading({
  eyebrow,
  title,
  linkLabel,
  linkHref,
  onLinkClick,
}: {
  eyebrow?: string;
  title: string;
  linkLabel?: string;
  linkHref?: string;
  onLinkClick?: () => void;
}) {
  return (
    <div className="sz-section-head">
      <div>
        {eyebrow && <p className="sz-eyebrow">{eyebrow}</p>}
        <h2>{title}</h2>
      </div>
      {linkLabel && linkHref && (
        <a href={linkHref} className="sz-link-caps" onClick={onLinkClick}>
          {linkLabel}
        </a>
      )}
    </div>
  );
}

export function LoadingBlock({ label = 'Loading', rows = 3 }: { label?: string; rows?: number }) {
  return (
    <div role="status" aria-live="polite" style={{ padding: '24px 0' }}>
      <span className="sz-visually-hidden">{label}…</span>
      <div style={{ display: 'grid', gap: 12 }}>
        {Array.from({ length: rows }).map((_, index) => (
          <div key={index} className="sz-skeleton" style={{ height: 18, width: `${90 - index * 12}%` }} />
        ))}
      </div>
    </div>
  );
}

export function CardSkeletonGrid({ count = 6, height = 260 }: { count?: number; height?: number }) {
  return (
    <div
      role="status"
      aria-live="polite"
      style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: 24 }}
    >
      <span className="sz-visually-hidden">Loading…</span>
      {Array.from({ length: count }).map((_, index) => (
        <div key={index} className="sz-skeleton" style={{ height }} />
      ))}
    </div>
  );
}

export function EmptyState({ message, action }: { message: string; action?: ReactNode }) {
  return (
    <div className="sz-empty">
      <p style={{ margin: '0 0 16px' }}>{message}</p>
      {action}
    </div>
  );
}

/** Turns any thrown error into a readable message without leaking internals. */
export function ErrorState({
  error,
  onRetry,
  title = 'Something went wrong',
}: {
  error: unknown;
  onRetry?: () => void;
  title?: string;
}) {
  const message =
    error instanceof ApiError
      ? error.message
      : 'We could not load this right now. Please try again.';

  return (
    <div className="sz-notice sz-notice--error" role="alert" style={{ margin: '24px 0' }}>
      <p style={{ margin: '0 0 8px', color: 'var(--sz-white)', fontSize: 14 }}>{title}</p>
      <p style={{ margin: 0 }}>{message}</p>
      {onRetry && (
        <button type="button" className="sz-btn sz-btn--ghost" style={{ marginTop: 16 }} onClick={onRetry}>
          Try again
        </button>
      )}
    </div>
  );
}

/** Inline validation / submission feedback for forms. */
export function FormMessage({ error, success }: { error?: unknown; success?: string }) {
  if (success) {
    return (
      <p role="status" style={{ color: 'var(--sz-gold)', fontSize: 13, margin: 0 }}>
        {success}
      </p>
    );
  }
  if (!error) return null;

  const message = error instanceof ApiError ? error.message : 'Something went wrong. Please try again.';
  return (
    <p role="alert" style={{ color: 'var(--sz-terracotta)', fontSize: 13, margin: 0 }}>
      {message}
    </p>
  );
}

export function Field({
  label,
  htmlFor,
  error,
  hint,
  children,
}: {
  label: string;
  htmlFor: string;
  error?: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <div className="sz-field">
      <label className="sz-label" htmlFor={htmlFor}>
        {label}
      </label>
      {children}
      {hint && !error && <p className="sz-field-hint">{hint}</p>}
      {error && (
        <p className="sz-field-error" id={`${htmlFor}-error`} role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

export function StepPips({ total, current }: { total: number; current: number }) {
  return (
    <div className="sz-steps" role="group" aria-label={`Step ${current} of ${total}`}>
      {Array.from({ length: total }).map((_, index) => {
        const step = index + 1;
        const state = step === current ? 'current' : step < current ? 'done' : 'todo';
        return <div key={step} className="sz-step-pip" data-state={state} aria-hidden="true" />;
      })}
    </div>
  );
}
